/**
 * Orchestrator for the sibling-order filtering pipeline.
 * @module siblingOrderFilter/getFilteredSiblingOrdersForParent
 */
import { GeneralOrderDetails } from "../../../data-model/medicalPaOrdersModel";
import { SiblingOrder } from "../../../data-model/siblingOrdersModel";
import { getAllJCodesFromAuthOnFile } from "../../../pages/singleOrderLayout/utils/existingDocsToLs";
import { evaluateSiblingAgainstJCode } from "./evaluateSiblingAgainstJCode";
import {
  filterSiblingOrdersByPrimaryCoveragePlanDates,
  ParentOrderCoverageDetails,
} from "./filterSiblingOrdersByPrimaryCoveragePlanDates";
import { areJCodesExactlySame, isAuthWindowStatus } from "./jCodeMatching";
import { describeOrder, logger, logPassed, logRejected } from "./logging";
import {
  filterOnlyNotWorkedOrders,
  sortSiblingOrdersByDosAscending,
} from "./orderFilters";
import { FilterJCodeInput } from "./types";

/**
 * Orchestrates the final sibling-order filtering for a parent medical PA order.
 *
 * Only siblings with the same non-empty payload location as the parent are
 * processed further. Location matching is exact, including case and whitespace.
 * The parent order and its J-codes act as the reference point. Each sibling
 * order is evaluated against every parent J-code; a sibling is kept only when
 * it satisfies all of them. Evaluation short-circuits on the first failing
 * J-code check, so a rejected sibling is dropped immediately without checking
 * the remaining J-codes.
 *
 * @param parentOrderData - General details of the parent order (source of the
 *   reference date of service and location)
 * @param parentOrderCoverageDetails - Parent order coverage details used to
 *   resolve the primary plan effective date range
 * @param parentJCodes - The parent order's J-codes, each carrying only the
 *   fields the pipeline consumes (see {@link FilterJCodeInput})
 * @param siblingOrders - The sibling orders to filter
 * @returns The sibling orders that satisfied every parent J-code's checks, in
 *   ascending date-of-service order
 */
export const getFilteredSiblingOrdersForParent = (
  parentOrderData: GeneralOrderDetails,
  parentOrderCoverageDetails: ParentOrderCoverageDetails | null | undefined,
  parentJCodes: FilterJCodeInput[],
  siblingOrders: SiblingOrder[],
): SiblingOrder[] => {
  logger.debug(
    `===== start: received ${siblingOrders.length} sibling order(s) =====`,
  );

  // Step 1: Extract the parent order's date of service — the reference point
  // for every month/year and "upcoming" comparison performed below.
  const parentOrderDos = parentOrderData?.payload?.date_of_service ?? "";
  logger.debug(`[Step 1] parent order dos=${parentOrderDos}`);

  // Step 1.a: Require a known, exact location match before further processing.
  const parentOrderLocation = parentOrderData?.payload?.location;
  const locationMatchedSiblingOrders = siblingOrders.filter(
    (sibling) =>
      Boolean(parentOrderLocation) &&
      sibling?.order?.payload?.location === parentOrderLocation,
  );
  logRejected(
    "[Step 1.a]",
    "location missing or does not exactly match parent location",
    siblingOrders,
    locationMatchedSiblingOrders,
  );
  logPassed(
    "[Step 1.a]",
    "location exactly matches parent location",
    locationMatchedSiblingOrders,
  );
  logger.debug(
    `[Step 1.a] location-matched orders: ${locationMatchedSiblingOrders.length}/${siblingOrders.length} passed`,
  );
  if (locationMatchedSiblingOrders.length === 0) {
    logger.debug(
      "[Step 1.a] no sibling orders with a matching location — result will be empty",
    );
    return [];
  }

  // Step 1.b: Sort sibling orders by date of service ascending so that any
  // later slicing (by remaining visits) keeps the earliest orders first.
  const sortedSiblingOrders = sortSiblingOrdersByDosAscending(
    locationMatchedSiblingOrders,
  );
  logger.debug(
    "[Step 1.b] sorted by dos ascending",
    sortedSiblingOrders.map(describeOrder),
  );

  // Step 2: Keep only sibling orders whose date of service falls within the
  // primary coverage plan's inclusive effective date range.
  const coveragePlanMatchedSiblingOrders =
    filterSiblingOrdersByPrimaryCoveragePlanDates(
      parentOrderCoverageDetails,
      sortedSiblingOrders,
    );
  if (coveragePlanMatchedSiblingOrders.length === 0) {
    logger.debug(
      "[Step 2] no sibling orders within primary coverage plan range — result will be empty",
    );
    return [];
  }

  // Step 2.a: Use the parent order's J-codes (supplied by the caller) as the
  // reference set for matching and per-J-code refinement.
  logger.debug(
    `[Step 2.a] parent j_codes=[${parentJCodes
      .map((jCode) => jCode?.j_code)
      .join(",")}]`,
  );
  if (parentJCodes.length === 0) {
    logger.warn("[Step 2.a] parent has no j_codes — result will be empty");
  }

  // Step 3: Keep only sibling orders that are still yet-to-start; drop the ones
  // that have already been worked on.
  const notYetWorkedSiblingOrders = filterOnlyNotWorkedOrders(
    coveragePlanMatchedSiblingOrders,
  );
  logRejected(
    "[Step 3]",
    "already worked (status moved past yet-to-start)",
    coveragePlanMatchedSiblingOrders,
    notYetWorkedSiblingOrders,
  );
  logPassed("[Step 3]", "still yet-to-start", notYetWorkedSiblingOrders);
  logger.debug(
    `[Step 3] not-yet-worked orders: ${notYetWorkedSiblingOrders.length}/${coveragePlanMatchedSiblingOrders.length} passed`,
  );

  // Step 4: Keep only the sibling orders whose J-code set exactly matches the
  // parent order's J-code set.
  const jCodeMatchedSiblingOrders = notYetWorkedSiblingOrders.filter(
    (sibling) => {
      const siblingJCodes =
        getAllJCodesFromAuthOnFile(sibling?.auth_on_file) ?? [];
      const isMatch = areJCodesExactlySame(parentJCodes, siblingJCodes);
      if (isMatch) {
        logger.debug(
          `[Step 4] PASSED (j_codes match): ${describeOrder(
            sibling,
          )} siblingJCodes=[${siblingJCodes
            .map((jCode) => jCode?.j_code)
            .join(",")}]`,
        );
      } else {
        logger.debug(
          `[Step 4] REJECTED (j_codes differ): ${describeOrder(
            sibling,
          )} siblingJCodes=[${siblingJCodes
            .map((jCode) => jCode?.j_code)
            .join(",")}]`,
        );
      }
      return isMatch;
    },
  );
  logger.debug(
    `[Step 4] j_code-matched orders: ${jCodeMatchedSiblingOrders.length}/${notYetWorkedSiblingOrders.length} passed`,
  );

  // Step 5: Evaluate each sibling order against every parent J-code. A sibling
  // is kept only when it satisfies all of them; the first failing check rejects
  // the sibling and moves evaluation on to the next order. Orders are processed
  // in ascending DOS order so the earliest ones consume each J-code's
  // remaining-visit budget first.
  const acceptedCountByJCode: Record<string, number> = {};
  const passedSiblingOrders: SiblingOrder[] = [];

  jCodeMatchedSiblingOrders.forEach((sibling, index) => {
    logger.debug(
      `[Step 5] evaluating sibling ${index + 1}/${jCodeMatchedSiblingOrders.length}: ${describeOrder(
        sibling,
      )}`,
    );
    const failingCheck = parentJCodes
      .map((jCode) => ({
        jCode,
        result: evaluateSiblingAgainstJCode(
          sibling,
          jCode,
          parentOrderDos,
          acceptedCountByJCode[jCode?.j_code ?? ""] ?? 0,
        ),
      }))
      .find(({ result }) => !result.passed);

    if (failingCheck) {
      logger.debug(
        `[Step 5] REJECTED (${failingCheck.result.reason}) by jcode=${
          failingCheck.jCode?.j_code ?? "n/a"
        }: ${describeOrder(sibling)}`,
      );
      return;
    }

    // Fully accepted: record it and consume budget for every window-bound
    // J-code (the order is within each such window, else it would have failed).
    passedSiblingOrders.push(sibling);
    parentJCodes.forEach((jCode) => {
      if (isAuthWindowStatus(jCode?.auth_status)) {
        const jCodeKey = jCode?.j_code ?? "";
        acceptedCountByJCode[jCodeKey] =
          (acceptedCountByJCode[jCodeKey] ?? 0) + 1;
      }
    });
    logger.debug(`[Step 5] PASSED all j_codes: ${describeOrder(sibling)}`);
  });

  logger.debug(
    `===== done: ${passedSiblingOrders.length}/${jCodeMatchedSiblingOrders.length} sibling order(s) passed =====`,
  );

  return passedSiblingOrders;
};
